import { db, functions } from '@/lib/firebase';
import { collection, getDocs, getDoc, updateDoc, query, where, doc, serverTimestamp, runTransaction, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { referralSchema, type Referral, ReferralStatus, type ReferralUIData, normalizeEmail, adminReferralSchema, type AdminReferral, type Candidate, candidateHiredSchema } from '@/lib/schema';
import { getJob } from './jobs';
import { getCandidateByEmail } from './candidates';
import { getUserByUid } from './users';

// Extended types for UI components
export interface ReferralWithJobInfo extends Referral {
  candidateName: string;
  jobTitle: string;
  referralDate: string;
  reward?: string;
  candidateAvatar?: string;
}

// Admin referral with UI data for management
export interface AdminReferralWithUI extends AdminReferral {
  candidateName: string;
  jobTitle: string;
  referrerName: string;
  matchingScore: number;
  priority: 'low' | 'normal' | 'high';
  candidateData?: Candidate | null;
}

export const getReferrals = async (filters: { jobId?: string; referrerId?: string; status?: ReferralStatus } = {}) => {
  let q = query(collection(db, 'referrals'));
  if (filters.jobId) q = query(q, where('jobId', '==', filters.jobId));
  if (filters.referrerId) q = query(q, where('referrerId', '==', filters.referrerId));
  if (filters.status) q = query(q, where('status', '==', filters.status));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => {
    const data = d.data();
    return referralSchema.parse({
      id: d.id,
      ...data,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
      reviewedAt: data.reviewedAt?.toDate(),
      interviewScheduledAt: data.interviewScheduledAt?.toDate(),
      hiredAt: data.hiredAt?.toDate(),
      rejectedAt: data.rejectedAt?.toDate(),
      statusHistory: data.statusHistory?.map((entry: any) => ({
        ...entry,
        changedAt: entry.changedAt?.toDate()
      })) || []
    });
  }) as Referral[];
};

// Get admin referrals with extended AdminReferral type
export const getAdminReferrals = async (filters: { jobId?: string; referrerId?: string; status?: ReferralStatus } = {}) => {
  let q = query(collection(db, 'referrals'));
  if (filters.jobId) q = query(q, where('jobId', '==', filters.jobId));
  if (filters.referrerId) q = query(q, where('referrerId', '==', filters.referrerId));
  if (filters.status) q = query(q, where('status', '==', filters.status));
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => {
    const data = d.data();
    return adminReferralSchema.parse({
      id: d.id,
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      reviewedAt: data.reviewedAt?.toDate(),
      interviewScheduledAt: data.interviewScheduledAt?.toDate(),
      hiredAt: data.hiredAt?.toDate(),
      rejectedAt: data.rejectedAt?.toDate(),
      matchAnalyzedAt: data.matchAnalyzedAt?.toDate(),
      statusHistory: data.statusHistory?.map((entry: any) => ({
        ...entry,
        changedAt: entry.changedAt?.toDate()
      })) || [],
      recruiterNote: data.recruiterNote?.map((note: any) => ({
        ...note,
        date: note.date?.toDate()
      })) || [],
      matchDetails: data.matchDetails ? {
        ...data.matchDetails,
        // matchDetails doesn't have date fields to convert
      } : undefined
    });
  }) as AdminReferral[];
};

export const checkDuplicateReferral = async (jobId: string, candidateEmail: string) => {
  const normalizedEmail = normalizeEmail(candidateEmail);
  const q = query(
    collection(db, 'referrals'),
    where('jobId', '==', jobId),
    where('candidateEmail', '==', normalizedEmail)
  );
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null; // No duplicate found
  }
  
  // Return the first matching referral
  const docSnap = snapshot.docs[0];
  const data = docSnap.data();
  return referralSchema.parse({
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
    reviewedAt: data.reviewedAt?.toDate(),
    interviewScheduledAt: data.interviewScheduledAt?.toDate(),
    hiredAt: data.hiredAt?.toDate(),
    rejectedAt: data.rejectedAt?.toDate(),
    statusHistory: data.statusHistory?.map((entry: any) => ({
      ...entry,
      changedAt: entry.changedAt?.toDate()
    })) || []
  }) as Referral;
};

export const getReferral = async (id: string) => {
  const refDoc = await getDoc(doc(db, 'referrals', id));
  if (!refDoc.exists()) return null;
  const data = refDoc.data();
  return referralSchema.parse({
    id: refDoc.id,
    ...data,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
    reviewedAt: data.reviewedAt?.toDate(),
    interviewScheduledAt: data.interviewScheduledAt?.toDate(),
    hiredAt: data.hiredAt?.toDate(),
    rejectedAt: data.rejectedAt?.toDate(),
    statusHistory: data.statusHistory?.map((entry: any) => ({
      ...entry,
      changedAt: entry.changedAt?.toDate()
    })) || []
  }) as Referral;
};

export const getReferralWithJob = async (id: string) => {
  const referral = await getReferral(id);
  if (!referral) return null;
  const job = await getJob(referral.jobId);
  return { ...referral, job };
};

// Create referral using Firebase callable function
export const createReferral = async (data: { 
  candidateUuid: string;
  candidateEmail: string; 
  jobId: string; 
  referralNote?: string; 
  relationship?: string; 
  candidateData: any;
  candidateLinkedin?: string;
  candidatePhone?: string;
}) => {
  // Try with custom timeout and additional options
  const createReferralCallable = httpsCallable(functions, 'createReferral', {
    timeout: 180000, // 3 minutes timeout for referral creation
    // Add additional options to help with response format
  });
  
  try {
    const payload = {
      candidateUuid: data.candidateUuid,
      candidateEmail: data.candidateEmail,
      jobId: data.jobId,
      referralNote: data.referralNote,
      relationship: data.relationship,
      // Include candidate data for processing
      candidateFirstName: data.candidateData.firstName,
      candidateLastName: data.candidateData.lastName,
      candidateLinkedin: data.candidateLinkedin || data.candidateData.linkedinUrl,
      candidatePhone: data.candidatePhone || data.candidateData.phoneNumber,
      candidateResumeUrl: data.candidateData.resumeUrl,
      candidateResumeFileName: data.candidateData.resumeFileName,
      // Add bonus points flag if resume is uploaded
      bonusPoints: !!data.candidateData.resumeUrl
    };
    
    const result = await createReferralCallable(payload);
    
    // Debug logging to understand the response structure
    
    // Handle different response formats from Firebase function
    const responseData = result.data as { success?: boolean; referralId?: string; data?: any } | string;
    
    // If the response is just a string (referralId), return it directly
    if (typeof responseData === 'string') {
      return responseData;
    }
    
    // Handle Firebase function response structure
    if (responseData && typeof responseData === 'object') {
      // Check if referralId is in the response object directly
      if ('referralId' in responseData && responseData.referralId) {
        return responseData.referralId;
      }
      
      // Check if the response has a nested data field with referralId
      if ('data' in responseData && responseData.data && typeof responseData.data === 'object') {
        if ('referralId' in responseData.data && responseData.data.referralId) {
          return responseData.data.referralId;
        }
      }
      
      // Check for success with referralId in any structure
      if (responseData.success && responseData.referralId) {
        return responseData.referralId;
      }
    }
    
    // Fallback error if response format is unexpected
    throw new Error(`Invalid response format from createReferral function. Expected referralId but received: ${JSON.stringify(responseData)}`);
  } catch (error: any) {
    
    // Handle Firebase function errors
    if (error.code === 'already-exists') {
      throw new Error('This candidate has already been referred to this position');
    } else if (error.code === 'failed-precondition') {
      throw new Error('Job is not available for referrals');
    } else if (error.code === 'permission-denied') {
      throw new Error('You do not have permission to create referrals');
    } else if (error.message && error.message.includes('missing data field')) {
      // This is a Firebase callable function response format error
      // The function may have succeeded but Firebase rejected the response format
      
      // Since this error suggests the function may have actually succeeded,
      // we'll treat this as a success but with a warning
      
      // Return a placeholder referral ID since we can't get the real one
      // The referral was likely created on the backend
      return 'referral-created-check-admin';
    } else if (error.code === 'functions/internal' && error.message.includes('missing data field')) {
      // Same handling for functions/internal error code
      
      // Return a placeholder referral ID since we can't get the real one
      return 'referral-created-check-admin';
    } else {
      throw new Error(error.message || 'Failed to create referral');
    }
  }
};

// Mark candidate as hired using Firebase callable function
export const onCandidateHired = async (data: {
  referralId: string;
  hiredDate: string;
  agreedSalary: string;
}) => {
  // Validate input data
  const validatedData = candidateHiredSchema.parse(data);
  
  const onCandidateHiredCallable = httpsCallable(functions, 'onCandidateHired', {
    timeout: 180000, // 3 minutes timeout for hire processing
  });
  
  try {
    const result = await onCandidateHiredCallable(validatedData);
    
    // Handle different response formats from Firebase function
    const responseData = result.data as { success?: boolean; message?: string; data?: any; referralId?: string; hiredDate?: string; agreedSalary?: string } | string;
    
    // If the response is successful with nested data structure
    if (typeof responseData === 'object' && responseData.success) {
      // Check if it has nested data field (your current response format)
      if (responseData.data) {
        return {
          success: true,
          message: responseData.data.message || 'Candidate successfully marked as hired',
          referralId: responseData.data.referralId,
          hiredDate: responseData.data.hiredDate,
          agreedSalary: responseData.data.agreedSalary
        };
      }
      // Direct success response
      return responseData;
    }
    
    // If the response has message and referralId (new format without success field)
    if (typeof responseData === 'object' && responseData.message && responseData.referralId) {
      return {
        success: true,
        message: responseData.message,
        referralId: responseData.referralId,
        hiredDate: responseData.hiredDate,
        agreedSalary: responseData.agreedSalary
      };
    }
    
    // If the response is just a success message string
    if (typeof responseData === 'string') {
      return { success: true, message: responseData };
    }
    
    // Fallback error if response format is unexpected
    throw new Error(`Invalid response format from onCandidateHired function. Received: ${JSON.stringify(responseData)}`);
  } catch (error: any) {
    // Handle Firebase function errors with more specific messages
    if (error.code === 'functions/not-found') {
      throw new Error('The onCandidateHired function was not found. Please contact support.');
    } else if (error.code === 'functions/internal') {
      throw new Error('Internal server error occurred while processing the hire. Please try again or contact support.');
    } else if (error.code === 'functions/unavailable') {
      throw new Error('The hire processing service is temporarily unavailable. Please try again in a few moments.');
    } else if (error.code === 'functions/deadline-exceeded') {
      throw new Error('The hire processing took too long. Please try again.');
    } else if (error.code === 'functions/unauthenticated') {
      throw new Error('You must be logged in to process hires.');
    } else if (error.code === 'functions/permission-denied') {
      throw new Error('You do not have permission to mark candidates as hired.');
    } else if (error.code === 'not-found') {
      throw new Error('Referral not found');
    } else if (error.code === 'failed-precondition') {
      throw new Error('Referral is not in a valid state to be marked as hired');
    } else if (error.code === 'already-exists') {
      throw new Error('This candidate has already been marked as hired');
    } else if (error.message?.includes('404')) {
      throw new Error('The onCandidateHired function was not found. Please ensure the Firebase function is deployed.');
    } else if (error.message?.includes('500')) {
      throw new Error('Server error occurred while processing the hire. Please try again or contact support.');
    } else {
      throw new Error(error.message || 'Failed to mark candidate as hired. Please try again.');
    }
  }
};

export const updateReferral = async (id: string, updates: Partial<Referral>) => {
  await updateDoc(doc(db, 'referrals', id), { ...updates, updatedAt: serverTimestamp() });
  return getReferral(id);
};

// Update referral status with history tracking
export const updateReferralStatus = async (
  referralId: string, 
  newStatus: ReferralStatus, 
  changedBy: string, 
  notes?: string,
  rejectionNotes?: string
) => {
  return await runTransaction(db, async (transaction) => {
    const referral = await getReferral(referralId);
    if (!referral) {
      throw new Error('Referral not found');
    }
    
    // Add to status history
    const statusEntry = {
      status: newStatus,
      changedAt: new Date(),
      changedBy,
      notes: notes || `Status changed to ${newStatus}`
    };
    
    const updates: any = {
      status: newStatus,
      statusHistory: [...referral.statusHistory, statusEntry],
      updatedAt: serverTimestamp()
    };
    
    // Set milestone timestamps
    if (newStatus === ReferralStatus.INTERVIEWED && !referral.interviewScheduledAt) {
      updates.interviewScheduledAt = serverTimestamp();
    }
    
    if (newStatus === ReferralStatus.HIRED && !referral.hiredAt) {
      updates.hiredAt = serverTimestamp();
      updates.rewardStatus = 'eligible'; // Mark reward as eligible
      
      // Update job current hires count
      const job = await getJob(referral.jobId);
      if (job) {
        const jobRef = doc(db, 'jobs', referral.jobId);
        const newHireCount = job.currentHires + 1;
        transaction.update(jobRef, {
          currentHires: newHireCount,
          updatedAt: serverTimestamp(),
          // Auto-close job if target reached
          ...(newHireCount >= job.targetHires && { status: 'closed' })
        });
      }
    }
    
    // Set rejection fields when status is REJECTED
    if (newStatus === ReferralStatus.REJECTED) {
      updates.rejectedAt = serverTimestamp();
      updates.rejectedBy = changedBy;
      if (rejectionNotes) {
        updates.rejectionNotes = rejectionNotes;
      }
    }
    
    const referralRef = doc(db, 'referrals', referralId);
    transaction.update(referralRef, updates);
    
    return referralId;
  }).then(async (id) => {
    return getReferral(id);
  });
};

// Add recruiter note to admin referral
export const addRecruiterNote = async (
  referralId: string,
  note: string,
  createdBy?: string
) => {
  return await runTransaction(db, async (transaction) => {
    const referralRef = doc(db, 'referrals', referralId);
    const referralSnap = await transaction.get(referralRef);
    
    if (!referralSnap.exists()) {
      throw new Error('Referral not found');
    }
    
    const referralData = referralSnap.data();
    const existingNotes = referralData.recruiterNote || [];
    
    // Create new note object
    const newNote = {
      note,
      date: new Date(),
      createdBy: createdBy || null
    };
    
    // Add the new note to the array
    const updatedNotes = [...existingNotes, newNote];
    
    // Update the referral
    transaction.update(referralRef, {
      recruiterNote: updatedNotes,
      updatedAt: serverTimestamp()
    });
    
    return newNote;
  });
};

// Subscribe to real-time referral updates
export const subscribeToReferral = (referralId: string, callback: (referral: Referral | null) => void) => {
  const referralRef = doc(db, 'referrals', referralId);
  
  return onSnapshot(referralRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      const referral = referralSchema.parse({
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        statusHistory: data.statusHistory?.map((entry: any) => ({
          ...entry,
          changedAt: entry.changedAt?.toDate() || new Date()
        })) || []
      });
      callback(referral);
    } else {
      callback(null);
    }
  }, (error) => {
    callback(null);
  });
};

// Subscribe to real-time referral list updates for a user (UI data only)
export const subscribeToUserReferralsUIData = (referrerId: string, callback: (referrals: ReferralUIData[]) => void) => {
  const q = query(
    collection(db, 'referrals'),
    where('referrerId', '==', referrerId)
  );
  
  return onSnapshot(q, async (querySnapshot) => {
    try {
      // Transform to UI data format
      const referralsUIData = await Promise.all(
        querySnapshot.docs.map(async (docSnap) => {
          try {
            const data = docSnap.data();
            
            // Only extract UI-relevant fields
            const referralData = {
              id: docSnap.id,
              candidateEmail: data.candidateEmail,
              jobId: data.jobId,
              status: data.status,
              rewardStatus: data.rewardStatus,
              rewardAmount: data.rewardAmount,
              referralNote: data.referralNote,
              relationship: data.relationship,
              bonusPoints: data.bonusPoints,
              rejectionNotes: data.rejectionNotes,
              createdAt: data.createdAt?.toDate() || new Date(),
            };
            
            const [job, candidate] = await Promise.all([
              getJob(referralData.jobId),
              getCandidateByEmail(referralData.candidateEmail)
            ]);
            
            
            return {
              id: referralData.id,
              candidateName: candidate 
                ? `${candidate.firstName} ${candidate.lastName}` 
                : referralData.candidateEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
              candidateEmail: referralData.candidateEmail,
              jobTitle: job?.title || 'Unknown Job',
              jobId: referralData.jobId,
              // companyName removed - users should not see company information
              jobCategory: job?.category,
              jobLocation: job?.location,
              jobRemoteType: job?.remoteType,
              jobDescription: job?.description,
              jobEmploymentType: job?.employmentType,
              jobSkills: job?.skills || [],
              status: referralData.status === ReferralStatus.PENDING ? ReferralStatus.SUBMITTED : referralData.status, // Show pending as submitted in UI
              rewardStatus: referralData.rewardStatus,
              rewardAmount: referralData.rewardAmount,
              referralDate: referralData.createdAt.toLocaleDateString(),
              referralNote: referralData.referralNote,
              relationship: referralData.relationship,
              candidateAvatar: undefined, // Candidates don't have profile images in current schema
              reward: job?.rewardInAirPoints ? `${job.rewardInAirPoints}` : undefined,
              bonusPoints: referralData.bonusPoints,
              rejectionNotes: referralData.rejectionNotes,
            } as ReferralUIData;
          } catch (error) {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              candidateName: data.candidateEmail 
                ? data.candidateEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                : 'Unknown Candidate',
              candidateEmail: data.candidateEmail || '',
              jobTitle: 'Unknown Job',
              jobId: data.jobId || '',
              // companyName removed - users should not see company information
              jobCategory: undefined,
              jobLocation: undefined,
              jobRemoteType: undefined,
              jobDescription: undefined,
              jobEmploymentType: undefined,
              jobSkills: [],
              status: data.status === ReferralStatus.PENDING ? ReferralStatus.SUBMITTED : data.status, // Show pending as submitted in UI
              rewardStatus: data.rewardStatus,
              rewardAmount: data.rewardAmount,
              referralDate: data.createdAt?.toDate()?.toLocaleDateString() || '',
              referralNote: data.referralNote,
              relationship: data.relationship,
              bonusPoints: data.bonusPoints,
              rejectionNotes: data.rejectionNotes,
            } as ReferralUIData;
          }
        })
      );
      
      callback(referralsUIData);
    } catch (error) {
      callback([]);
    }
  }, (error) => {
    callback([]);
  });
};

// Legacy subscription function - kept for backward compatibility but deprecated
// @deprecated Use subscribeToUserReferralsUIData instead for better performance and security
export const subscribeToUserReferrals = (referrerId: string, callback: (referrals: Referral[]) => void) => {
  const q = query(
    collection(db, 'referrals'),
    where('referrerId', '==', referrerId)
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const referrals = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return referralSchema.parse({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        statusHistory: data.statusHistory?.map((entry: any) => ({
          ...entry,
          changedAt: entry.changedAt?.toDate() || new Date()
        })) || []
      });
    });
    callback(referrals);
  }, (error) => {
    callback([]);
  });
};

// Get admin referral status counts for dashboard
export const getAdminReferralStatusCounts = async () => {
  const snapshot = await getDocs(collection(db, 'referrals'));
  const referrals = snapshot.docs.map(d => d.data());
  
  return {
    submitted: referrals.filter(r => r.status === ReferralStatus.SUBMITTED).length,
    shortlisted: referrals.filter(r => r.status === ReferralStatus.SHORTLISTED).length,
    interviewed: referrals.filter(r => r.status === ReferralStatus.INTERVIEWED).length,
    under_review: referrals.filter(r => r.status === ReferralStatus.UNDER_REVIEW).length,
  };
};

// Subscribe to real-time admin referral status counts
export const subscribeToAdminReferralStatusCounts = (callback: (counts: { submitted: number; shortlisted: number; interviewed: number; under_review: number }) => void) => {
  return onSnapshot(collection(db, 'referrals'), (snapshot) => {
    const referrals = snapshot.docs.map(d => d.data());
    
    const counts = {
      submitted: referrals.filter(r => r.status === ReferralStatus.SUBMITTED).length,
      shortlisted: referrals.filter(r => r.status === ReferralStatus.SHORTLISTED).length,
      interviewed: referrals.filter(r => r.status === ReferralStatus.INTERVIEWED).length,
      under_review: referrals.filter(r => r.status === ReferralStatus.UNDER_REVIEW).length,
    };
    
    callback(counts);
  }, (error) => {
    callback({ submitted: 0, shortlisted: 0, interviewed: 0, under_review: 0 });
  });
};

// Get admin referrals with UI data for management dashboard
export const getAdminReferralsWithUI = async (filters: { jobId?: string; referrerId?: string; status?: ReferralStatus } = {}): Promise<AdminReferralWithUI[]> => {
  let q = query(collection(db, 'referrals'));
  if (filters.jobId) q = query(q, where('jobId', '==', filters.jobId));
  if (filters.referrerId) q = query(q, where('referrerId', '==', filters.referrerId));
  if (filters.status) q = query(q, where('status', '==', filters.status));
  
  const snapshot = await getDocs(q);
  
  // Transform to admin referrals with UI data
  const adminReferralsWithUI = await Promise.all(
    snapshot.docs.map(async (docSnap) => {
      try {
        const data = docSnap.data();
        
        
        // Parse admin referral data
        const adminReferral = adminReferralSchema.parse({
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          hiredAt: data.hiredAt?.toDate(),
          rejectedAt: data.rejectedAt?.toDate(),
          matchAnalyzedAt: data.matchAnalyzedAt?.toDate(),
          statusHistory: data.statusHistory?.map((entry: any) => ({
            ...entry,
            changedAt: entry.changedAt?.toDate()
          })) || [],
          recruiterNote: data.recruiterNote?.map((note: any) => ({
            ...note,
            date: note.date?.toDate()
          })) || []
        });
        
        // Fetch related data
        const [job, candidate, referrer] = await Promise.all([
          getJob(adminReferral.jobId),
          getCandidateByEmail(adminReferral.candidateEmail),
          getUserByUid(adminReferral.referrerId)
        ]);
        
        // Create candidate name from candidate data
        let candidateName = 'Unknown Candidate';
        if (candidate) {
          candidateName = `${candidate.firstName} ${candidate.lastName}`;
        } else {
          // Fallback to email-based name
          candidateName = adminReferral.candidateEmail
            .split('@')[0]
            .replace(/[._-]/g, ' ')
            .replace(/\b\w/g, (l: string) => l.toUpperCase());
        }
        
        return {
          ...adminReferral,
          candidateName,
          jobTitle: job?.title || 'Unknown Job',
          referrerName: referrer ? `${referrer.firstName} ${referrer.lastName}` : 'Unknown Referrer',
          matchingScore: adminReferral.matchScore || 0,
          priority: 'normal' as const, // Default priority
          candidateData: candidate, // Include full candidate data
        } as AdminReferralWithUI;
      } catch (error) {
        // Return fallback data
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          candidateName: data.candidateEmail 
            ? data.candidateEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
            : 'Unknown Candidate',
          jobTitle: 'Unknown Job',
          referrerName: 'Unknown Referrer',
          matchingScore: 0,
          priority: 'normal' as const,
          candidateData: null, // No candidate data available in error case
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          statusHistory: data.statusHistory || [],
        } as AdminReferralWithUI;
      }
    })
  );

  return adminReferralsWithUI;
};

// Subscribe to real-time admin referrals with UI data
export const subscribeToAdminReferralsWithUI = (callback: (referrals: AdminReferralWithUI[]) => void, filters: { jobId?: string; referrerId?: string; status?: ReferralStatus } = {}) => {
  let q = query(collection(db, 'referrals'));
  if (filters.jobId) q = query(q, where('jobId', '==', filters.jobId));
  if (filters.referrerId) q = query(q, where('referrerId', '==', filters.referrerId));
  if (filters.status) q = query(q, where('status', '==', filters.status));
  
  return onSnapshot(q, async (querySnapshot) => {
    try {
      // Transform to admin referrals with UI data
      const adminReferralsWithUI = await Promise.all(
        querySnapshot.docs.map(async (docSnap) => {
          try {
            const data = docSnap.data();
            
            
            // Parse admin referral data
            const adminReferral = adminReferralSchema.parse({
              id: docSnap.id,
              ...data,
              createdAt: data.createdAt?.toDate(),
              updatedAt: data.updatedAt?.toDate(),
              hiredAt: data.hiredAt?.toDate(),
              rejectedAt: data.rejectedAt?.toDate(),
              matchAnalyzedAt: data.matchAnalyzedAt?.toDate(),
              statusHistory: data.statusHistory?.map((entry: any) => ({
                ...entry,
                changedAt: entry.changedAt?.toDate()
              })) || [],
              recruiterNote: data.recruiterNote?.map((note: any) => ({
                ...note,
                date: note.date?.toDate()
              })) || []
            });
            
            // Fetch related data
            const [job, candidate, referrer] = await Promise.all([
              getJob(adminReferral.jobId),
              getCandidateByEmail(adminReferral.candidateEmail),
              getUserByUid(adminReferral.referrerId)
            ]);
            
            // Create candidate name from candidate data
            let candidateName = 'Unknown Candidate';
            if (candidate) {
              candidateName = `${candidate.firstName} ${candidate.lastName}`;
            } else {
              // Fallback to email-based name
              candidateName = adminReferral.candidateEmail
                .split('@')[0]
                .replace(/[._-]/g, ' ')
                .replace(/\b\w/g, (l: string) => l.toUpperCase());
            }
            
            return {
              ...adminReferral,
              candidateName,
              jobTitle: job?.title || 'Unknown Job',
              referrerName: referrer ? `${referrer.firstName} ${referrer.lastName}` : 'Unknown Referrer',
              matchingScore: adminReferral.matchScore || 0,
              priority: 'normal' as const, // Default priority
              candidateData: candidate, // Include full candidate data
            } as AdminReferralWithUI;
          } catch (error) {
            // Return fallback data
            const data = docSnap.data();
            return {
              id: docSnap.id,
              ...data,
              candidateName: data.candidateEmail 
                ? data.candidateEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                : 'Unknown Candidate',
              jobTitle: 'Unknown Job',
              referrerName: 'Unknown Referrer',
              matchingScore: 0,
              priority: 'normal' as const,
              candidateData: null, // No candidate data available in error case
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
              statusHistory: data.statusHistory || [],
            } as AdminReferralWithUI;
          }
        })
      );
      
      callback(adminReferralsWithUI);
    } catch (error) {
      callback([]);
    }
  }, (error) => {
    callback([]);
  });
};

// Get referrals with only UI-relevant data (no sensitive matching/analysis data)
export const getReferralsUIData = async (filters: { jobId?: string; referrerId?: string; status?: ReferralStatus } = {}): Promise<ReferralUIData[]> => {
  let q = query(collection(db, 'referrals'));
  if (filters.jobId) q = query(q, where('jobId', '==', filters.jobId));
  if (filters.referrerId) q = query(q, where('referrerId', '==', filters.referrerId));
  if (filters.status) q = query(q, where('status', '==', filters.status));
  
  const snapshot = await getDocs(q);
  
  // Fetch job and candidate information for each referral
  const referralsUIData = await Promise.all(
    snapshot.docs.map(async (docSnap) => {
      try {
        const data = docSnap.data();
        
        // Only extract UI-relevant fields from the referral document
        const referralData = {
          id: docSnap.id,
          candidateEmail: data.candidateEmail,
          jobId: data.jobId,
          status: data.status,
          rewardStatus: data.rewardStatus,
          rewardAmount: data.rewardAmount,
          referralNote: data.referralNote,
          relationship: data.relationship,
          bonusPoints: data.bonusPoints,
          rejectionNotes: data.rejectionNotes,
          createdAt: data.createdAt?.toDate() || new Date(),
        };
        
        const [job, candidate] = await Promise.all([
          getJob(referralData.jobId),
          getCandidateByEmail(referralData.candidateEmail)
        ]);
        
        return {
          id: referralData.id,
          candidateName: candidate 
            ? `${candidate.firstName} ${candidate.lastName}` 
            : referralData.candidateEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
          candidateEmail: referralData.candidateEmail,
          jobTitle: job?.title || 'Unknown Job',
          jobId: referralData.jobId,
          // companyName removed - users should not see company information
          jobCategory: job?.category,
          jobLocation: job?.location,
          jobRemoteType: job?.remoteType,
          jobDescription: job?.description,
          jobEmploymentType: job?.employmentType,
          jobSkills: job?.skills || [],
          status: referralData.status === ReferralStatus.PENDING ? ReferralStatus.SUBMITTED : referralData.status, // Show pending as submitted in UI
          rewardStatus: referralData.rewardStatus,
          rewardAmount: referralData.rewardAmount,
          referralDate: referralData.createdAt.toLocaleDateString(),
          referralNote: referralData.referralNote,
          relationship: referralData.relationship,
          candidateAvatar: undefined, // Candidates don't have profile images in current schema
          reward: job?.rewardInAirPoints ? `${job.rewardInAirPoints}` : undefined,
          bonusPoints: referralData.bonusPoints,
          rejectionNotes: referralData.rejectionNotes,
        } as ReferralUIData;
      } catch (error) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          candidateName: data.candidateEmail 
            ? data.candidateEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
            : 'Unknown Candidate',
          candidateEmail: data.candidateEmail || '',
          jobTitle: 'Unknown Job',
          jobId: data.jobId || '',
          // companyName removed - users should not see company information
          jobCategory: undefined,
          jobLocation: undefined,
          jobRemoteType: undefined,
          jobDescription: undefined,
          jobEmploymentType: undefined,
          jobSkills: [],
          status: data.status === ReferralStatus.PENDING ? ReferralStatus.SUBMITTED : data.status, // Show pending as submitted in UI
          rewardStatus: data.rewardStatus,
          rewardAmount: data.rewardAmount,
          referralDate: data.createdAt?.toDate()?.toLocaleDateString() || '',
          referralNote: data.referralNote,
          relationship: data.relationship,
          bonusPoints: data.bonusPoints,
          rejectionNotes: data.rejectionNotes,
        } as ReferralUIData;
      }
    })
  );

  return referralsUIData;
};

// Legacy function - kept for backward compatibility but deprecated
// @deprecated Use getReferralsUIData instead for better performance and security
export const getReferralsWithJobInfo = async (filters: { jobId?: string; referrerId?: string; status?: ReferralStatus } = {}): Promise<ReferralWithJobInfo[]> => {
  const referrals = await getReferrals(filters);
  
  // Fetch job and candidate information for each referral
  const referralsWithJobInfo = await Promise.all(
    referrals.map(async (referral) => {
      try {
        const [job, candidate] = await Promise.all([
          getJob(referral.jobId),
          getCandidateByEmail(referral.candidateEmail)
        ]);
        
        return {
          ...referral,
          candidateName: candidate 
            ? `${candidate.firstName} ${candidate.lastName}` 
            : referral.candidateEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
          jobTitle: job?.title || 'Unknown Job',
          // company field removed as per memory: users should not see company information
          referralDate: referral.createdAt.toLocaleDateString(),
          reward: job?.rewardInAirPoints ? `${job.rewardInAirPoints}` : undefined, // Remove "AirPoints" text
        } as ReferralWithJobInfo;
      } catch (error) {
        return {
          ...referral,
          candidateName: referral.candidateEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
          jobTitle: 'Unknown Job',
          referralDate: referral.createdAt.toLocaleDateString(),
          reward: undefined,
        } as ReferralWithJobInfo;
      }
    })
  );

  return referralsWithJobInfo;
};
